import LazyImage from "../components/LazyImage";
import * as MetadataView from '../features/metadataView';
import { isRenderableImageSrc } from '../../../utils/imageUtils';
import { collectRowGalleryImages } from "./galleryNavigation";

/**
 * Core Column Types - Professional Airtable-like rendering
 *
 * text     - Basic text content
 * number   - Numeric values
 * price    - Currency/price values
 * date     - Date values
 * link     - URLs and links
 * image    - Image URLs
 * email    - Email addresses
 * address  - Address information
 * metadata - Metadata/additional information
 */

/**
 * Extract domain from URL for display
 */
const extractDomain = (url) => {
  try {
    const urlObj = new URL(url);
    return urlObj.hostname.replace(/^www\./, '');
  } catch {
    return url;
  }
};

/**
 * Expand a cell value into a list of image URLs. A single image cell can hold
 * several comma-separated URLs (e.g. the "All Images" column). Split only when a
 * comma is followed by another http(s) URL so single URLs that legitimately
 * contain commas aren't broken.
 */
export const expandImageUrls = (value) => {
  if (!value || typeof value !== "string" || !value.trim()) return [];
  // Drop anything that isn't an absolute image reference. An "image" column can
  // still hold plain text (a mis-detected column, a description scraped into
  // it), and handing that to <img src> makes the browser request it as a path
  // relative to the current page. Callers fall back to rendering text when this
  // comes back empty.
  return splitUrlList(value).filter(isRenderableImageSrc);
};

/**
 * Currency symbols and codes for detection
 */
const CURRENCY_SYMBOLS = [
  '$',      // Dollar (USD, CAD, AUD, etc.)
  '€',      // Euro
  '£',      // British Pound
  '¥',      // Yen/Yuan
  '₹',      // Indian Rupee
  '₩',      // Korean Won
  '₽',      // Russian Ruble
  '₺',      // Turkish Lira
  '₴',      // Ukrainian Hryvnia
  '₦',      // Nigerian Naira
  '₱',      // Philippine Peso
  '฿',      // Thai Baht
  '₫',      // Vietnamese Dong
  '₡',      // Costa Rican Colón
  '₲',      // Paraguayan Guaraní
  '₵',      // Ghanaian Cedi
  '₸',      // Kazakhstani Tenge
  '₮',      // Mongolian Tugrik
  '₼',      // Azerbaijani Manat
  '₾',      // Georgian Lari
  '₿',      // Bitcoin
  'R$',     // Brazilian Real
  'kr',     // Scandinavian Krone
  'zł',     // Polish Zloty
  'Kč',     // Czech Koruna
  'Ft',     // Hungarian Forint
  'lei',    // Romanian Leu
  'лв',     // Bulgarian Lev
  'RM',     // Malaysian Ringgit
  'S$',     // Singapore Dollar
  'HK$',    // Hong Kong Dollar
  'NT$',    // Taiwan Dollar
  'R',      // South African Rand
  'CHF',    // Swiss Franc
];

const CURRENCY_CODES = [
  'USD', 'EUR', 'GBP', 'JPY', 'CAD', 'AUD', 'NZD', 'CHF', 'CNY', 'HKD',
  'SGD', 'INR', 'MXN', 'BRL', 'KRW', 'TWD', 'THB', 'MYR', 'IDR', 'PHP',
  'VND', 'RUB', 'TRY', 'PLN', 'SEK', 'NOK', 'DKK', 'CZK', 'HUF', 'RON',
  'BGN', 'HRK', 'ISK', 'ILS', 'AED', 'SAR', 'QAR', 'KWD', 'BHD', 'OMR',
  'ZAR', 'EGP', 'NGN', 'KES', 'GHS', 'PKR', 'BDT', 'LKR', 'NPR', 'MMK',
  'ARS', 'CLP', 'COP', 'PEN', 'UYU', 'VEF', 'BOB', 'PYG', 'CRC', 'GTQ',
  'UAH', 'KZT', 'GEL', 'AZN', 'AMD', 'BYN', 'MDL', 'UZS', 'TJS', 'KGS',
  'BTC', 'ETH', 'USDT', 'USDC',
];

/**
 * Check if a string value represents a currency/price
 */
export const isCurrencyValue = (value) => {
  if (typeof value !== 'string') return false;
  const trimmed = value.trim();

  // Check for currency symbol at start (e.g., $100, €50, £30)
  const symbolsEscaped = CURRENCY_SYMBOLS.map(s => s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|');
  const symbolStartPattern = new RegExp(`^(${symbolsEscaped})\\s?[\\d,.\\s]+$`, 'i');
  if (symbolStartPattern.test(trimmed)) return true;

  // Check for currency symbol at end (e.g., 100€, 50 kr)
  const symbolEndPattern = new RegExp(`^[\\d,.\\s]+\\s?(${symbolsEscaped})$`, 'i');
  if (symbolEndPattern.test(trimmed)) return true;

  // Check for currency code at start or end (e.g., USD 100, 100 USD)
  const codesPattern = CURRENCY_CODES.join('|');
  const codeStartPattern = new RegExp(`^(${codesPattern})\\s?[\\d,.\\s]+$`, 'i');
  const codeEndPattern = new RegExp(`^[\\d,.\\s]+\\s?(${codesPattern})$`, 'i');
  if (codeStartPattern.test(trimmed) || codeEndPattern.test(trimmed)) return true;

  return false;
};

/**
 * Check if a string value looks like a phone number.
 *
 * The hard part is rejecting the ids, SKUs and prices that look phone-ish
 * ("11894", "71113-002887", "18530.00") without dropping real international
 * numbers (E.164 allows up to 15 digits). The discriminator: phones are
 * formatted with +, parentheses, spaces, or the canonical US 3-3-4 hyphen
 * pattern — ids are bare digit runs and SKUs use bare hyphens and dots.
 *
 * Set the column type to "phone" to force the phone treatment regardless.
 */
const isPhoneValue = (value) => {
  if (typeof value !== "string") return false;
  const trimmed = value.trim();

  // Letters => not a phone (excludes alphanumeric SKUs like "AB967-A001")
  if (/[a-zA-Z]/.test(trimmed)) return false;

  // Allowed chars: digits, +, (), spaces, hyphens. Note: no dot — a dot signals a
  // decimal/price ("18530.00"), never a real phone separator here.
  if (!/^[+]?[(]?[\d\s()\-]+$/.test(trimmed)) return false;

  // E.164: 7–15 digits.
  const digitCount = (trimmed.match(/\d/g) || []).length;
  if (digitCount < 7 || digitCount > 15) return false;

  // Strong signal: a leading + or parentheses is unambiguously a phone format.
  if (/^\+/.test(trimmed) || /[()]/.test(trimmed)) {
    return /^[+]?[(]?\d{1,4}[)]?([-\s]?[(]?\d{1,4}[)]?){1,5}$/.test(trimmed);
  }

  // No + or parens: a bare digit run is an id ("11894", "1234567890") and a bare
  // hyphen run is almost always a SKU ("71113-002887"). Only accept space-grouped
  // digits (phones use spaces, ids and SKUs don't) or the canonical US 3-3-4 /
  // 3-4 hyphen pattern.
  const usHyphen = /^\d{3}-\d{3}-\d{4}$/.test(trimmed) || /^\d{3}-\d{4}$/.test(trimmed);
  const hasSpaceGroup = /\d\s\d/.test(trimmed);
  if (usHyphen || hasSpaceGroup) {
    return /^\d{1,4}([-\s]\d{1,4}){1,5}$/.test(trimmed);
  }

  return false;
};

/**
 * Split a cell value into individual URLs when it holds several comma-separated
 * ones. Only splits on commas that precede another http(s) URL so single URLs
 * containing commas stay intact. Returns [value] when there's just one.
 */
const splitUrlList = (value) => {
  const trimmed = value.trim();
  if (/,\s*https?:\/\//i.test(trimmed)) {
    return trimmed
      .split(/,\s*(?=https?:\/\/)/i)
      .map((u) => u.trim())
      .filter(Boolean);
  }
  return [trimmed];
};

/**
 * Render a single URL as a domain + path link with an external-link icon.
 */
const renderUrlLink = (url, isDarkMode, key) => {
  const domain = extractDomain(url);
  let pathPortion = '';
  try {
    const urlObj = new URL(url);
    pathPortion = urlObj.pathname + urlObj.search + urlObj.hash;
    if (pathPortion === '/') pathPortion = '';
  } catch {
    // If URL parsing fails, just show domain
  }

  return (
    <a
      key={key}
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      onClick={(e) => e.stopPropagation()}
      className={`inline-flex items-center gap-1 max-w-full group/link transition-colors duration-150`}
    >
      <span className={`flex-shrink-0 text-xs ${isDarkMode ? "text-blue-400 group-hover/link:text-blue-300" : "text-blue-600 group-hover/link:text-blue-700"}`}>
        {domain}
      </span>
      {pathPortion && (
        <span className={`truncate text-xs ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}>
          {pathPortion}
        </span>
      )}
      <svg
        className={`w-3 h-3 flex-shrink-0 transition-opacity ${isDarkMode ? "text-gray-500 group-hover/link:text-blue-400" : "text-gray-400 group-hover/link:text-blue-600"}`}
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
      </svg>
    </a>
  );
};

/**
 * Format date for display
 */
const formatDate = (dateStr) => {
  try {
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return dateStr;
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  } catch {
    return dateStr;
  }
};

/**
 * Render a cell value based on its type and content
 * @param {Object} item Data row
 * @param {string} columnId Column identifier
 * @param {string} columnType Column type
 * @param {Object} column Column definition
 * @param {Array} columns All columns (needed for image previews)
 * @param {boolean} isDarkMode Current theme mode
 * @param {Function} onOpenGallery Optional callback to open image gallery (images, initialIndex, column)
 * @returns {JSX.Element} Rendered cell content
 */
export const renderDataCellValue = (item, columnId, columnType, column, columns, isDarkMode = true, onOpenGallery = null) => {
  if (!item || !item.data) return null;

  // Handle special column types - both show image previews
  if (columnId === "image_group_toggle" || columnId === "image_previews") {
    // Expand each image column's cell into its individual URLs so a single cell
    // holding comma-separated URLs contributes multiple thumbnails.
    const allImageUrls = collectRowGalleryImages(item, column, columns, expandImageUrls);

    // First 3 shown as thumbnails; the rest roll up into a "+N" overflow button.
    const imageUrls = allImageUrls.slice(0, 3);
    const nonEmptyImageCount = allImageUrls.length;

    return (
      <div className="flex items-center gap-1.5 overflow-visible w-fit">
        {!imageUrls.length ? (
          <span className={`text-xs ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}>—</span>
        ) : (
          <>
            {imageUrls.map((url, idx) => (
              <button
                key={idx}
                onClick={(e) => {
                  e.stopPropagation();
                  if (onOpenGallery) {
                    onOpenGallery(allImageUrls, idx, column);
                  } else {
                    window.open(url, '_blank');
                  }
                }}
                className="block relative group/img cursor-pointer"
              >
                <LazyImage
                  url={url}
                  alt={`Preview ${idx + 1}`}
                  className={`h-10 w-10 object-cover rounded transition-all duration-150 ${
                    isDarkMode
                      ? "ring-1 ring-white/10 group-hover/img:ring-2 group-hover/img:ring-yellow-400/70"
                      : "ring-1 ring-gray-200 group-hover/img:ring-2 group-hover/img:ring-yellow-400/70"
                  }`}
                  isDarkMode={isDarkMode}
                />
              </button>
            ))}
            {nonEmptyImageCount > 3 && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  if (onOpenGallery) {
                    onOpenGallery(allImageUrls, 3, column);
                  }
                }}
                className={`h-10 min-w-[40px] px-2 flex items-center justify-center rounded text-xs font-medium cursor-pointer transition-all duration-150 ${
                  isDarkMode
                    ? "bg-white/5 text-gray-400 hover:bg-white/10 hover:text-gray-300"
                    : "bg-gray-100 text-gray-500 hover:bg-gray-200 hover:text-gray-700"
                }`}
              >
                +{nonEmptyImageCount - 3}
              </button>
            )}
          </>
        )}
      </div>
    );
  }

  // Handle metadata column types (status, http, duration, error, json)
  const metadataResult = MetadataView.renderMetadataCell(columnType, item.data[columnId], isDarkMode);
  if (metadataResult.handled) {
    return metadataResult.element;
  }

  // Handle grouped columns
  if (column && column.isGrouped && column.originalColumns) {
    // Try to find a non-empty value from any of the grouped columns
    for (const origCol of column.originalColumns) {
      const value = item.data[origCol.id];
      if (value !== undefined && value !== null && value !== "") {
        return renderDataCellValue(item, origCol.id, origCol.type, origCol, columns, isDarkMode, onOpenGallery);
      }
    }
    // If all values are empty
    return <span className={`text-xs ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}>—</span>;
  }

  const value = item.data[columnId];

  if (value === undefined || value === null || value === "") {
    return <span className={`text-xs ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}>—</span>;
  }

  // Handle different data types
  if (typeof value === "string") {
    // Only image columns split a comma-separated list of URLs into thumbnails.
    // In every other column type, a comma-separated list of URLs is just several
    // links and must render as links (below), never as (broken) images.
    const isImageColumn = columnType === "image";

    // Check if it's a URL (or a comma-separated list of URLs) in a non-image column.
    if (value.startsWith("http") && !isImageColumn) {
      const urls = splitUrlList(value);
      if (urls.length > 1) {
        // Multiple links — render each as its own clickable domain link.
        return (
          <span className="inline-flex flex-wrap items-center gap-x-2 gap-y-1">
            {urls.flatMap((url, i) => {
              const link = renderUrlLink(url, isDarkMode, url + i);
              return i > 0
                ? [<span key={`sep-${i}`} className={`text-xs ${isDarkMode ? "text-gray-500" : "text-gray-400"}`}>,</span>, link]
                : [link];
            })}
          </span>
        );
      }
      return renderUrlLink(value, isDarkMode);
    }

    // Check if it's an image URL (or a comma-separated list of them).
    // Non-image columns already returned above; here we only reach image
    // columns and value-based image detection (a URL ending in an image
    // extension, or containing "/image"). Those two hints only mean anything
    // on an actual URL — a paragraph that happens to end in ".png" or mention
    // "/image" is text.
    const looksLikeImageValue =
      isRenderableImageSrc(value) &&
      (/\.(jpeg|jpg|gif|png|webp)$/i.test(value.trim()) || value.includes("/image"));
    if (isImageColumn || looksLikeImageValue) {
      const urls = expandImageUrls(value);
      if (urls.length) {
        const shown = urls.slice(0, 3);
        return (
          <div className="flex items-center gap-1.5 overflow-visible w-fit">
            {shown.map((url, idx) => (
              <button
                key={idx}
                onClick={(e) => {
                  e.stopPropagation();
                  if (onOpenGallery) {
                    onOpenGallery(urls, idx, column);
                  } else {
                    window.open(url, '_blank');
                  }
                }}
                className="block relative group/img cursor-pointer"
              >
                <LazyImage
                  url={url}
                  alt={urls.length > 1 ? `Preview ${idx + 1}` : "Preview"}
                  className={`h-10 max-w-[100px] object-contain rounded transition-all duration-150 ${
                    isDarkMode
                      ? "ring-1 ring-white/10 group-hover/img:ring-2 group-hover/img:ring-yellow-400/70"
                      : "ring-1 ring-gray-200 group-hover/img:ring-2 group-hover/img:ring-yellow-400/70"
                  }`}
                  isDarkMode={isDarkMode}
                />
              </button>
            ))}
            {urls.length > 3 && (
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  if (onOpenGallery) {
                    onOpenGallery(urls, 3, column);
                  }
                }}
                className={`h-10 min-w-[40px] px-2 flex items-center justify-center rounded text-xs font-medium cursor-pointer transition-all duration-150 ${
                  isDarkMode
                    ? "bg-white/5 text-gray-400 hover:bg-white/10 hover:text-gray-300"
                    : "bg-gray-100 text-gray-500 hover:bg-gray-200 hover:text-gray-700"
                }`}
              >
                +{urls.length - 3}
              </button>
            )}
          </div>
        );
      }
    }

    // Check if it's a date
    if (value.match(/^\d{4}-\d{2}-\d{2}/) || value.match(/^\d{2}\/\d{2}\/\d{4}/) || value.match(/^\w+ \d{1,2}, \d{4}/)) {
      const formattedDate = formatDate(value);
      return (
        <span className={`text-xs ${isDarkMode ? "text-teal-400" : "text-teal-600"}`}>
          {formattedDate}
        </span>
      );
    }

    // Check if it's a price or currency
    if (isCurrencyValue(value)) {
      return (
        <span className={`text-xs font-medium tabular-nums ${isDarkMode ? "text-emerald-400" : "text-emerald-600"}`}>
          {value}
        </span>
      );
    }

    // Check if it's one or more comma-separated email addresses
    const emailRe = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    const emailParts = value.split(',').map((s) => s.trim()).filter(Boolean);
    if (emailParts.length > 0 && emailParts.every((part) => emailRe.test(part))) {
      return (
        <span
          className={`inline-flex items-center gap-1.5 text-xs ${
            isDarkMode ? "text-blue-400" : "text-blue-600"
          }`}
        >
          <svg className="w-3.5 h-3.5 flex-shrink-0 opacity-60" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
          </svg>
          <span className="truncate">
            {emailParts.flatMap((email, i) => {
              const link = (
                <a
                  key={email}
                  href={`mailto:${email}`}
                  onClick={(e) => e.stopPropagation()}
                  className={`transition-colors duration-150 ${
                    isDarkMode ? "hover:text-blue-300" : "hover:text-blue-700"
                  }`}
                >
                  {email}
                </a>
              );
              return i > 0 ? [<span key={`sep-${email}`}>, </span>, link] : [link];
            })}
          </span>
        </span>
      );
    }

    // Check if it's a phone number. Only "phone" columns get forced; auto-detect
    // is limited to untyped/text columns so typed columns (e.g. price) never get
    // the phone icon and tel: link when a value happens to look phone-ish. See
    // isPhoneValue for how ids, SKUs and prices are kept out of auto-detection.
    const phoneAutoDetectable = !columnType || columnType === "text";
    if (columnType === "phone" || (phoneAutoDetectable && isPhoneValue(value))) {
      return (
        <a
          href={`tel:${value.replace(/[^\d+]/g, '')}`}
          onClick={(e) => e.stopPropagation()}
          className={`inline-flex items-center gap-1.5 text-xs transition-colors duration-150 ${
            isDarkMode ? "text-gray-300 hover:text-white" : "text-gray-700 hover:text-gray-900"
          }`}
        >
          <svg className="w-3.5 h-3.5 flex-shrink-0 opacity-60" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
          </svg>
          <span>{value}</span>
        </a>
      );
    }

    // For other strings, show with smart truncation
    const maxLength = 80;
    if (value.length > maxLength) {
      return (
        <span className={`text-xs leading-relaxed ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}>
          {value.substring(0, maxLength)}
          <span className={`${isDarkMode ? "text-gray-500" : "text-gray-400"}`}>...</span>
        </span>
      );
    }

    return (
      <span className={`text-xs leading-relaxed ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}>
        {value}
      </span>
    );
  }

  // Handle numbers
  if (typeof value === "number") {
    // Check if it looks like a price/currency number
    const isPrice = columnType === "price" || columnType === "currency";

    if (isPrice) {
      return (
        <span className={`text-xs font-medium tabular-nums ${isDarkMode ? "text-emerald-400" : "text-emerald-600"}`}>
          ${value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
        </span>
      );
    }

    return (
      <span className={`text-xs tabular-nums ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}>
        {value.toLocaleString()}
      </span>
    );
  }

  // Handle booleans
  if (typeof value === "boolean") {
    return value ? (
      <span
        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${
          isDarkMode
            ? "bg-emerald-500/15 text-emerald-400"
            : "bg-emerald-50 text-emerald-700"
        }`}
      >
        <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
        </svg>
        Yes
      </span>
    ) : (
      <span
        className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${
          isDarkMode
            ? "bg-gray-500/15 text-gray-400"
            : "bg-gray-100 text-gray-500"
        }`}
      >
        <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
          <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
        </svg>
        No
      </span>
    );
  }

  if (typeof value === "object") {
    // Use the MetadataView's JsonPopover for objects
    const result = MetadataView.renderMetadataCell('json', value, isDarkMode);
    if (result.handled) {
      return result.element;
    }
  }

  return <span className={`text-xs ${isDarkMode ? "text-gray-300" : "text-gray-700"}`}>{String(value)}</span>;
};
